import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/auth")({
  staticData: { sitemap: false },
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign in · Youtubers Map" },
      { name: "description", content: "Accedeix al backoffice de Youtubers Map." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/admin" });
    });
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/admin" },
        });
        if (error) throw error;
        toast.success("Compte creat. T'has convertit en admin (primer usuari).");
        navigate({ to: "/admin" });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/admin" });
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SiteNav />
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-paper ring-hairline p-10 shadow-xl">
          <p className="text-mono-caps text-amber mb-3">
            {mode === "signup" ? "Create the admin account" : "Sign in"}
          </p>
          <h1 className="text-3xl mb-2">
            {mode === "signup" ? "Take command" : "Welcome back"}
          </h1>
          <p className="text-sm text-ink/60 mb-8">
            {mode === "signup"
              ? "El primer usuari que es registri es converteix en admin del projecte."
              : "Accedeix al backoffice de Youtubers Map."}
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-mono-caps text-ink/50 block mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full ring-hairline bg-paper px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber"
              />
            </div>
            <div>
              <label className="text-mono-caps text-ink/50 block mb-1.5">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full ring-hairline bg-paper px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-ink text-paper font-medium py-3 hover:bg-amber transition-colors disabled:opacity-50"
            >
              {busy ? "…" : mode === "signup" ? "Sign up" : "Sign in"}
            </button>
          </form>
          <button
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="mt-6 text-sm text-ink/60 hover:text-amber transition-colors w-full text-center"
          >
            {mode === "signin" ? "Don't have an account? Sign up →" : "Already have one? Sign in →"}
          </button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
